import { Quote } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { MilestonesRibbon } from "@/components/home/MilestonesRibbon";
import { BIO_NARRATIVE, BOOK, QUOTE } from "@/data/content";

export const AboutSection = () => (
  <section id="about" data-testid="about-section" className="bg-white py-24 md:py-32">
    <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
      <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <MaskedLine>
            <span className="font-serif text-lg italic text-slate-500">The Full Story</span>
          </MaskedLine>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-ink sm:text-5xl">
            <MaskedLine delay={0.1}>
              <span>ABOUT JAMES PRATT</span>
            </MaskedLine>
          </h2>
          <Reveal delay={0.2}>
            <div className="mt-7 max-w-2xl lg:max-w-xl">
              {BIO_NARRATIVE.paragraphs.map((p, i) => (
                <p
                  key={i}
                  data-testid={`about-paragraph-${i + 1}`}
                  className="mt-5 text-sm leading-relaxed text-slate-600 first:mt-0 md:text-base"
                >
                  {p}
                </p>
              ))}
              <div data-testid="about-status" className="mt-8 border-t border-slate-200 pt-6">
                <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-navy">
                  {BIO_NARRATIVE.statusLabel}
                </p>
                <p className="text-sm leading-relaxed text-slate-600 md:text-base">
                  {BIO_NARRATIVE.status}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5">
          <figure className="w-full max-w-md lg:ml-auto lg:max-w-[340px]">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
              <img
                src="/assets/james-pratt-about-main.png"
                alt="James Pratt, outdoor portrait"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <figcaption
              data-testid="about-portrait-caption"
              className="mt-2 text-center font-serif text-sm italic text-slate-500"
            >
              James Pratt.
            </figcaption>
          </figure>
          <figure className="mt-6 w-full max-w-md lg:max-w-[340px] lg:ml-auto">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
              <img
                src="/assets/james-pratt-family.png"
                alt="James Pratt with his family"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <figcaption
              data-testid="about-family-caption"
              className="mt-2 text-center font-serif text-sm italic text-slate-500"
            >
              Celebrating family, the foundation behind every journey and achievement.
            </figcaption>
          </figure>
          <figure className="mt-6 w-full max-w-md lg:max-w-[340px] lg:ml-auto">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
              <img
                src="/assets/james-pratt-graduation.jpg"
                alt="James Pratt with family at a graduation celebration"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <figcaption
              data-testid="about-graduation-caption"
              className="mt-2 text-center font-serif text-sm italic text-slate-500"
            >
              A proud family moment, celebrating the next generation.
            </figcaption>
          </figure>
        </Reveal>
      </div>

      <MilestonesRibbon />

      <Reveal delay={0.1}>
        <figure
          data-testid="about-founder-quote"
          className="noise relative mt-16 overflow-hidden rounded-2xl bg-navy-ink p-10 md:p-14"
        >
          <Quote size={26} style={{ color: "#7FA8E8" }} />
          <blockquote className="mt-5 font-serif text-xl italic leading-relaxed text-white md:text-2xl">
            &ldquo;{QUOTE.text}&rdquo;
          </blockquote>
          <figcaption className="mt-6 text-xs font-semibold uppercase tracking-widest text-zinc-400">
            {QUOTE.attribution}
          </figcaption>
        </figure>
      </Reveal>
      <Reveal delay={0.1}>
        <div
          data-testid="about-book-feature"
          className="mt-16 grid grid-cols-1 items-center gap-10 rounded-2xl border border-slate-100 bg-white p-10 shadow-sm md:grid-cols-12 md:p-14"
        >
          <div className="md:col-span-4">
            <a
              href={BOOK.pdf}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-book-link"
              aria-label="Read the book Why a Basement Leaks (opens PDF in a new tab)"
              className="group mx-auto block w-full max-w-[240px] cursor-pointer"
            >
              <div
                className="relative overflow-hidden rounded-md transition-transform duration-300 group-hover:-translate-y-1.5"
                style={{
                  boxShadow:
                    "0 30px 60px -15px rgba(10, 10, 11, 0.35), 0 12px 24px -10px rgba(15, 61, 129, 0.25)",
                }}
              >
                <img
                  src={BOOK.cover}
                  alt="Cover of the forthcoming book Why a Basement Leaks by James Pratt"
                  className="h-auto w-full"
                />
                <div
                  className="pointer-events-none absolute inset-y-0 left-0 w-2.5 bg-gradient-to-r from-black/20 to-transparent"
                  aria-hidden="true"
                />
              </div>
              <p
                data-testid="about-book-cue"
                className="mt-5 text-center text-xs font-semibold uppercase tracking-widest text-navy transition-colors group-hover:text-navy-dark"
              >
                {BOOK.cue}
              </p>
            </a>
          </div>
          <div className="text-center md:col-span-8 md:text-left">
            <p
              data-testid="about-book-label"
              className="text-xs font-semibold uppercase tracking-widest text-navy"
            >
              {BOOK.label}
            </p>
            <h3 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-ink sm:text-4xl">
              {BOOK.title}
            </h3>
            <p className="mx-auto mt-4 max-w-xl font-serif text-base italic leading-relaxed text-slate-500 md:mx-0 md:text-lg">
              {BOOK.supporting}
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
